/**
 * The chat's glyphs, traced from the canon (`docs/design/czat-web-2026-10-05/`). Every one is
 * decorative: the control it sits in names itself.
 */

import type { ReactNode } from 'react';

function Glyph({ size, width, children }: { size: number; width?: number; children: ReactNode }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={width ?? 1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {children}
    </svg>
  );
}

export function BubbleGlyph({ size }: { size?: number }) {
  return (
    <Glyph size={size ?? 17}>
      <path d="M20.5 11.4c0 4-3.8 7.1-8.5 7.1-1.2 0-2.3-.2-3.3-.6L4 19.5l1.4-3.7a6.5 6.5 0 0 1-1.9-4.4c0-4 3.8-7.1 8.5-7.1s8.5 3.1 8.5 7.1Z" />
    </Glyph>
  );
}

export function PenGlyph({ size }: { size?: number }) {
  return (
    <Glyph size={size ?? 18}>
      <path d="M4 20h4L19 9a2.8 2.8 0 0 0-4-4L4 16Z" />
      <path d="m13.5 6.5 4 4" />
    </Glyph>
  );
}

export function SearchGlyph({ size }: { size?: number }) {
  return (
    <Glyph size={size ?? 16}>
      <circle cx="11" cy="11" r="6.5" />
      <path d="m20 20-4.3-4.3" />
    </Glyph>
  );
}

export function ImageGlyph({ size }: { size?: number }) {
  return (
    <Glyph size={size ?? 13}>
      <rect x="3.5" y="4.5" width="17" height="15" rx="3" />
      <circle cx="9" cy="10" r="1.8" />
      <path d="m20.5 16-4.6-4.6a1.5 1.5 0 0 0-2.1 0L5 20" />
    </Glyph>
  );
}

export function VideoGlyph({ size }: { size?: number }) {
  return (
    <Glyph size={size ?? 13}>
      <rect x="3" y="6" width="13" height="12" rx="2.5" />
      <path d="m16 10.5 5-3v9l-5-3" />
    </Glyph>
  );
}

export function MicGlyph({ size }: { size?: number }) {
  return (
    <Glyph size={size ?? 13}>
      <rect x="9" y="3.5" width="6" height="11" rx="3" />
      <path d="M5.5 11.5a6.5 6.5 0 0 0 13 0M12 18v2.5" />
    </Glyph>
  );
}

export function DumbbellGlyph({ size, width }: { size?: number; width?: number }) {
  return (
    <Glyph size={size ?? 13} width={width}>
      <path d="M6.5 8v8M17.5 8v8M4 10.5v3M20 10.5v3M6.5 12h11" />
    </Glyph>
  );
}

export function LayersGlyph({ size, width }: { size?: number; width?: number }) {
  return (
    <Glyph size={size ?? 13} width={width}>
      <path d="m12 4 8.5 4.5L12 13 3.5 8.5Z" />
      <path d="m3.5 12.5 8.5 4.5 8.5-4.5M3.5 16.5 12 21l8.5-4.5" />
    </Glyph>
  );
}

export function CalendarGlyph({ size, width }: { size?: number; width?: number }) {
  return (
    <Glyph size={size ?? 13} width={width}>
      <rect x="3.5" y="5" width="17" height="15.5" rx="3" />
      <path d="M3.5 9.8h17M8.2 3v3.8M15.8 3v3.8" />
    </Glyph>
  );
}

export function CameraGlyph({ size, width }: { size?: number; width?: number }) {
  return (
    <Glyph size={size ?? 13} width={width}>
      <path d="M4 8.5A2.5 2.5 0 0 1 6.5 6h1.8l1.4-2h4.6l1.4 2h1.8A2.5 2.5 0 0 1 20 8.5v8a2.5 2.5 0 0 1-2.5 2.5h-11A2.5 2.5 0 0 1 4 16.5Z" />
      <circle cx="12" cy="12.5" r="3.4" />
    </Glyph>
  );
}

export function PlayGlyph({ size }: { size?: number }) {
  return (
    <Glyph size={size ?? 13}>
      <path d="M8 5.5v13l10.5-6.5Z" fill="currentColor" stroke="none" />
    </Glyph>
  );
}

export function PauseGlyph({ size }: { size?: number }) {
  return (
    <Glyph size={size ?? 16} width={3}>
      <path d="M8 5.5v13M16 5.5v13" />
    </Glyph>
  );
}

export function DoubleTickGlyph({ size }: { size?: number }) {
  return (
    <Glyph size={size ?? 14}>
      <path d="m2.5 12.5 4 4L15 8M10 15.5l1 1L21.5 6.5" />
    </Glyph>
  );
}

export function ClockGlyph({ size, width }: { size?: number; width?: number }) {
  return (
    <Glyph size={size ?? 12} width={width ?? 1.7}>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 7.5V12l3 2" />
    </Glyph>
  );
}

export function AlertGlyph({ size }: { size?: number }) {
  return (
    <Glyph size={size ?? 13}>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7.5v5.5M12 16.4v.1" />
    </Glyph>
  );
}

export function PersonGlyph({ size }: { size?: number }) {
  return (
    <Glyph size={size ?? 15}>
      <circle cx="12" cy="8.5" r="3.8" />
      <path d="M4.5 20c.6-3.8 3.6-6 7.5-6s6.9 2.2 7.5 6" />
    </Glyph>
  );
}

export function ClipGlyph({ size }: { size?: number }) {
  return (
    <Glyph size={size ?? 19}>
      <path d="M20.5 11.2 12 19.7a5.2 5.2 0 0 1-7.4-7.4l8.7-8.7a3.5 3.5 0 0 1 4.9 4.9l-8.6 8.6a1.7 1.7 0 0 1-2.5-2.5l7.9-7.9" />
    </Glyph>
  );
}

export function SendGlyph({ size }: { size?: number }) {
  return (
    <Glyph size={size ?? 18} width={2.2}>
      <path d="M4.5 12h13M12.5 6l6 6-6 6" />
    </Glyph>
  );
}

export function ArrowGlyph({ size }: { size?: number }) {
  return (
    <Glyph size={size ?? 14} width={2}>
      <path d="M5 12h14M13 6l6 6-6 6" />
    </Glyph>
  );
}

export function ArrowDownGlyph({ size }: { size?: number }) {
  return (
    <Glyph size={size ?? 14} width={2}>
      <path d="M12 5v14M6 13l6 6 6-6" />
    </Glyph>
  );
}

export function CloseGlyph({ size }: { size?: number }) {
  return (
    <Glyph size={size ?? 15} width={2}>
      <path d="M6 6l12 12M18 6 6 18" />
    </Glyph>
  );
}

export function BackGlyph({ size }: { size?: number }) {
  return (
    <Glyph size={size ?? 16} width={2}>
      <path d="M15 5.5 8.5 12l6.5 6.5" />
    </Glyph>
  );
}

export function TrashGlyph({ size }: { size?: number }) {
  return (
    <Glyph size={size ?? 17}>
      <path d="M4.5 7h15M9.5 7V4.8h5V7M6.5 7l1 12.5h9l1-12.5" />
    </Glyph>
  );
}

export function StopGlyph({ size }: { size?: number }) {
  return (
    <Glyph size={size ?? 16}>
      <rect x="7" y="7" width="10" height="10" rx="2" fill="currentColor" stroke="none" />
    </Glyph>
  );
}

export function GreetGlyph({ size }: { size?: number }) {
  return (
    <Glyph size={size ?? 15}>
      <path d="M5 5.5h14v10H10l-5 4Z" />
    </Glyph>
  );
}

export function SplitGlyph({ size }: { size?: number }) {
  return (
    <Glyph size={size ?? 18}>
      <rect x="3.5" y="4.5" width="17" height="15" rx="3" />
      <path d="M14.5 4.5v15" />
    </Glyph>
  );
}
